import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

function clampPercent(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(100, num));
}

function formatPercent(value, digits = 1) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `${num.toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  })}%`;
}

function isConfirmedMatrixCell(cell) {
  if (!cell) return false;
  // Only own month stamp or explicitly copied value counts as collected.
  // Soft carry from previous month (can_copy) must not inflate progress.
  return Boolean(cell.is_own || cell.is_copied);
}

function isCarriedMatrixCell(cell) {
  if (!cell || isConfirmedMatrixCell(cell)) return false;
  return Boolean(cell.is_carried);
}

export function buildProgressFromMatrix({ rows = [], month, minimumPercent = 50.0001 } = {}) {
  const activeRows = (rows || []).filter((row) => !row?.archived);
  const requiredRows = activeRows.filter((row) => !row?.heat_calculate_by_area);
  const optionalRows = activeRows.filter((row) => row?.heat_calculate_by_area);

  const confirmedRequired = requiredRows.filter((row) =>
    isConfirmedMatrixCell(row?.values?.[month])
  ).length;
  const carriedRequired = requiredRows.filter((row) =>
    isCarriedMatrixCell(row?.values?.[month])
  ).length;
  const confirmedOptional = optionalRows.filter((row) =>
    isConfirmedMatrixCell(row?.values?.[month])
  ).length;
  const totalRequired = requiredRows.length;
  const percent = totalRequired > 0 ? (confirmedRequired / totalRequired) * 100 : 0;
  const threshold = Number(minimumPercent) > 0 ? Number(minimumPercent) : 50.0001;
  const ok = percent + 1e-9 >= threshold;
  const missing = Math.max(totalRequired - confirmedRequired, 0);
  const readyLabel =
    confirmedRequired === 0 && carriedRequired > 0
      ? 'Есть данные прошлого месяца — скопируйте, чтобы учесть в прогрессе'
      : ok
        ? 'Покрытие достаточное для расчёта'
        : 'Ниже порога — сбор ещё идёт';

  return {
    percent,
    threshold,
    ok,
    filled: confirmedRequired,
    total: totalRequired,
    missing,
    confirmed: confirmedRequired,
    carried: carriedRequired,
    byAreaCount: optionalRows.length,
    byAreaFilled: confirmedOptional,
    source: 'matrix',
    readyLabel,
    chips: [
      {
        key: 'devices',
        label: `Закреплено: ${confirmedRequired}/${totalRequired || 0}`,
        tone: confirmedRequired > 0 && missing === 0 ? 'success' : 'default',
      },
      carriedRequired > 0
        ? {
            key: 'carried',
            label: `Не закреплено (из прошлого): ${carriedRequired}`,
            tone: 'warning',
          }
        : null,
      missing - carriedRequired > 0
        ? {
            key: 'missing',
            label: `Пусто: ${missing - carriedRequired}`,
            tone: 'warning',
          }
        : null,
      optionalRows.length
        ? { key: 'area', label: `По площади: ${optionalRows.length}` }
        : null,
    ].filter(Boolean),
    issues: [],
  };
}

export function buildProgressFromReportSummary(summary) {
  if (!summary) return null;
  const percent = clampPercent(summary.readings_coverage_percent);
  const threshold = Number(summary.minimum_required_coverage_percent) > 0
    ? Number(summary.minimum_required_coverage_percent)
    : 50.0001;
  const ok = Boolean(summary.coverage_ok) || percent + 1e-9 >= threshold;

  return {
    percent,
    threshold,
    ok,
    filled: null,
    total: null,
    missing: null,
    source: 'report',
    chips: [
      {
        key: 'coverage',
        label: `Покрытие площади: ${formatPercent(percent, 2)}`,
        tone: ok ? 'success' : 'warning',
      },
      summary.data_area != null
        ? {
            key: 'data-area',
            label: `Площадь с данными: ${Number(summary.data_area).toLocaleString('ru-RU', {
              maximumFractionDigits: 1,
            })} м²`,
          }
        : null,
      summary.missing_area != null && Number(summary.missing_area) > 0
        ? {
            key: 'missing-area',
            label: `Без данных: ${Number(summary.missing_area).toLocaleString('ru-RU', {
              maximumFractionDigits: 1,
            })} м²`,
            tone: 'warning',
          }
        : null,
    ].filter(Boolean),
    issues: [],
  };
}

export function buildProgressFromReadiness(readiness) {
  if (!readiness?.summary) return null;
  const summary = readiness.summary;
  const percent = clampPercent(summary.readings_coverage_percent);
  const threshold = Number(summary.minimum_required_coverage_percent) > 0
    ? Number(summary.minimum_required_coverage_percent)
    : 50.0001;
  const ok = Boolean(summary.is_ready) || percent + 1e-9 >= threshold;
  const missingReadings = Number(summary.missing_readings_count || 0);
  const missingCoeff = Number(summary.missing_coefficient_count || 0);
  const missingArea = Number(summary.missing_area_count || 0);
  const issues = Array.isArray(readiness.issues) ? readiness.issues : [];

  return {
    percent,
    threshold,
    ok: Boolean(summary.is_ready) ? true : ok && Number(summary.errors_count || 0) === 0,
    filled: Number(summary.devices_ready || 0),
    total: Number(summary.devices_total || 0),
    missing: missingReadings,
    source: 'readiness',
    readyLabel: summary.is_ready ? 'Готово к расчёту' : 'Нужна проверка',
    chips: [
      {
        key: 'devices',
        label: `УРТ готовы: ${summary.devices_ready}/${summary.devices_total}`,
        tone: summary.devices_ready === summary.devices_total ? 'success' : 'default',
      },
      missingReadings > 0
        ? { key: 'readings', label: `Нет показаний: ${missingReadings}`, tone: 'warning' }
        : null,
      missingCoeff > 0
        ? { key: 'coeff', label: `Нет коэфф.: ${missingCoeff}`, tone: 'warning' }
        : null,
      missingArea > 0
        ? { key: 'area', label: `Нет площади: ${missingArea}`, tone: 'error' }
        : null,
      Number(summary.errors_count || 0) > 0
        ? { key: 'errors', label: `Ошибки: ${summary.errors_count}`, tone: 'error' }
        : null,
      Number(summary.warnings_count || 0) > 0
        ? { key: 'warnings', label: `Предупреждения: ${summary.warnings_count}`, tone: 'warning' }
        : null,
    ].filter(Boolean),
    issues,
  };
}

function toneColor(tone, ok) {
  if (tone === 'error') return { fg: '#B91C1C', bg: 'rgba(254, 226, 226, 0.9)', border: 'rgba(248, 113, 113, 0.45)' };
  if (tone === 'warning') return { fg: '#B45309', bg: 'rgba(255, 247, 237, 0.95)', border: 'rgba(251, 146, 60, 0.4)' };
  if (tone === 'success') return { fg: '#047857', bg: 'rgba(236, 253, 245, 0.95)', border: 'rgba(52, 211, 153, 0.4)' };
  if (ok) return { fg: '#047857', bg: 'rgba(240, 253, 244, 0.88)', border: 'rgba(52, 211, 153, 0.35)' };
  return { fg: '#B45309', bg: 'rgba(255, 251, 235, 0.92)', border: 'rgba(251, 191, 36, 0.4)' };
}

export default function HeatCollectionProgress({
  title = 'Сбор данных',
  periodLabel = '',
  progress = null,
  loading = false,
  dense = false,
  emptyText = 'Выберите период, чтобы увидеть прогресс сбора.',
  stats = null,
  periodOptions = null,
  selectedPeriodId = '',
  onPeriodSelect = null,
}) {
  const [issuesOpen, setIssuesOpen] = useState(false);
  const palette = useMemo(
    () => toneColor(progress?.ok ? 'success' : 'warning', Boolean(progress?.ok)),
    [progress?.ok]
  );
  const hasStats = Array.isArray(stats) && stats.length > 0;
  const hasPeriodOptions = Array.isArray(periodOptions) && periodOptions.length > 0;

  if (loading) {
    return (
      <Paper
        variant="outlined"
        sx={{
          px: dense ? 1.25 : 1.5,
          py: dense ? 1 : 1.25,
          borderRadius: 2.5,
          borderColor: 'rgba(38, 65, 94, 0.12)',
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Считаем прогресс сбора данных…
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (!progress) {
    return (
      <Paper
        variant="outlined"
        sx={{
          px: dense ? 1.25 : 1.5,
          py: dense ? 1 : 1.25,
          borderRadius: 2.5,
          borderColor: 'rgba(38, 65, 94, 0.12)',
          background: '#F8FAFC',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {emptyText}
        </Typography>
      </Paper>
    );
  }

  const percent = clampPercent(progress.percent);
  const barColor = progress.ok ? '#10B981' : '#F59E0B';
  const visibleIssues = (progress.issues || []).slice(0, 5);

  return (
    <Paper
      variant="outlined"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        px: dense ? 1.25 : 1.7,
        py: dense ? 1.05 : 1.45,
        borderRadius: 3,
        borderColor: palette.border,
        background: `linear-gradient(135deg, ${palette.bg} 0%, #FFFFFF 52%, #F8FBFF 100%)`,
        boxShadow: '0 10px 28px rgba(15, 23, 42, 0.04)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: barColor,
        }}
      />
      <Stack spacing={dense ? 0.85 : 1.2}>
        {hasPeriodOptions ? (
          <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap" alignItems="center">
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', mr: 0.25 }}>
              Период
            </Typography>
            {periodOptions.map((option) => {
              const selected = String(option.id) === String(selectedPeriodId);
              return (
                <Chip
                  key={option.id}
                  size="small"
                  clickable={Boolean(onPeriodSelect)}
                  onClick={() => onPeriodSelect?.(option.id)}
                  label={option.label}
                  sx={{
                    height: 26,
                    fontWeight: selected ? 800 : 650,
                    bgcolor: selected ? 'rgba(46, 115, 184, 0.14)' : 'rgba(248, 250, 252, 0.95)',
                    color: selected ? '#1D4ED8' : '#475569',
                    border: selected
                      ? '1px solid rgba(46, 115, 184, 0.42)'
                      : '1px solid rgba(38, 65, 94, 0.12)',
                  }}
                />
              );
            })}
          </Stack>
        ) : null}

        <Stack
          direction={{ xs: 'column', lg: hasStats ? 'row' : 'column' }}
          spacing={1.35}
          alignItems={{ xs: 'stretch', lg: hasStats ? 'center' : 'stretch' }}
        >
          <Stack spacing={dense ? 0.85 : 1.1} sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack spacing={0.15} sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B', lineHeight: 1.2 }}>
                  {title}
                  {periodLabel ? (
                    <Box component="span" sx={{ fontWeight: 600, color: '#64748B' }}>
                      {' · '}
                      {periodLabel}
                    </Box>
                  ) : null}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {progress.readyLabel
                    || (progress.ok ? 'Покрытие достаточное для расчёта' : 'Ниже порога — сбор ещё идёт')}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: dense ? 22 : 28,
                    lineHeight: 1,
                    color: palette.fg,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatPercent(percent, percent >= 99.95 || percent < 10 ? 1 : 0)}
                </Typography>
                <Chip
                  size="small"
                  label={progress.ok ? 'Достаточно' : 'В процессе'}
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    bgcolor: progress.ok ? 'rgba(16, 185, 129, 0.14)' : 'rgba(245, 158, 11, 0.16)',
                    color: palette.fg,
                    border: `1px solid ${palette.border}`,
                  }}
                />
              </Stack>
            </Stack>

            <Box sx={{ position: 'relative' }}>
              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{
                  height: dense ? 8 : 11,
                  borderRadius: 999,
                  backgroundColor: 'rgba(15, 23, 42, 0.06)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    background: `linear-gradient(90deg, ${barColor} 0%, ${
                      progress.ok ? '#34D399' : '#FBBF24'
                    } 100%)`,
                  },
                }}
              />
              {progress.threshold != null ? (
                <Box
                  title={`Порог ${formatPercent(progress.threshold, 2)}`}
                  sx={{
                    position: 'absolute',
                    top: -2,
                    bottom: -2,
                    left: `${clampPercent(progress.threshold)}%`,
                    width: 2,
                    ml: '-1px',
                    bgcolor: 'rgba(15, 23, 42, 0.35)',
                    borderRadius: 1,
                  }}
                />
              ) : null}
            </Box>

            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {(progress.chips || []).map((chip) => {
                const chipTone = toneColor(chip.tone, chip.tone === 'success');
                return (
                  <Chip
                    key={chip.key}
                    size="small"
                    label={chip.label}
                    sx={{
                      height: 22,
                      fontSize: 11.5,
                      fontWeight: 650,
                      bgcolor: chip.tone ? chipTone.bg : 'rgba(248, 250, 252, 0.95)',
                      color: chip.tone ? chipTone.fg : '#334155',
                      border: `1px solid ${chip.tone ? chipTone.border : 'rgba(38, 65, 94, 0.12)'}`,
                    }}
                  />
                );
              })}
              {progress.threshold != null ? (
                <Chip
                  size="small"
                  label={`Порог ${formatPercent(progress.threshold, 2)}`}
                  sx={{
                    height: 22,
                    fontSize: 11.5,
                    fontWeight: 650,
                    bgcolor: 'rgba(248, 250, 252, 0.95)',
                    color: '#475569',
                    border: '1px solid rgba(38, 65, 94, 0.12)',
                  }}
                />
              ) : null}
            </Stack>
          </Stack>

          {hasStats ? (
            <Stack
              direction="row"
              spacing={0.85}
              useFlexGap
              flexWrap="wrap"
              sx={{
                flex: { lg: '0 0 auto' },
                maxWidth: { lg: 420 },
              }}
            >
              {stats.map((stat) => (
                <Box
                  key={stat.key || stat.label}
                  sx={{
                    minWidth: 112,
                    flex: '1 1 112px',
                    px: 1.1,
                    py: 0.95,
                    borderRadius: 2.25,
                    border: '1px solid rgba(38, 65, 94, 0.10)',
                    background: 'rgba(255, 255, 255, 0.82)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, display: 'uppercase', letterSpacing: 0.3 }}>
                    {stat.label}
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: 18, color: '#0F172A', lineHeight: 1.15, mt: 0.35 }}>
                    {stat.value}
                  </Typography>
                  {stat.hint ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15 }}>
                      {stat.hint}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Stack>
          ) : null}
        </Stack>

        {visibleIssues.length > 0 ? (
          <Box>
            <Button
              size="small"
              onClick={() => setIssuesOpen((value) => !value)}
              endIcon={issuesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ px: 0.5, minWidth: 0, textTransform: 'none', fontWeight: 700 }}
            >
              {issuesOpen ? 'Скрыть замечания' : `Замечания (${progress.issues.length})`}
            </Button>
            <Collapse in={issuesOpen}>
              <Stack spacing={0.55} sx={{ mt: 0.75 }}>
                {visibleIssues.map((issue, index) => (
                  <Typography
                    key={`${issue.code}-${issue.device_id || issue.apartment_id || index}`}
                    variant="caption"
                    sx={{
                      color: issue.severity === 'error' ? '#B91C1C' : '#B45309',
                      lineHeight: 1.35,
                    }}
                  >
                    • {issue.message}
                    {issue.apartment_number ? ` · кв. ${issue.apartment_number}` : ''}
                  </Typography>
                ))}
                {progress.issues.length > visibleIssues.length ? (
                  <Typography variant="caption" color="text.secondary">
                    И ещё {progress.issues.length - visibleIssues.length}…
                  </Typography>
                ) : null}
              </Stack>
            </Collapse>
          </Box>
        ) : null}
      </Stack>
    </Paper>
  );
}
